import React from 'react';
import { CONCURRENT_TASK_TYPES, TaskDefinition, TaskNotification, UITask } from '@/context/house/tasks/types';
import type { UiTaskAction } from '@/context/house/tasks/state/uiTaskReducer';
import { actions as uiActions } from '@/context/house/tasks/state/uiTaskReducer';
import { makeDeleteKeyFromMeta } from '@/context/house/tasks/helpers';

/**
 * Produces the retry callbacks used by HouseTaskManagerContext.
 *
 * All selected tasks are removed from the queue snapshot atomically before
 * their replacements are scheduled. This avoids relying on React's passive
 * effect timing to refresh uiTasksRef between individual retry operations.
 */
export function createRetryActions(deps: {
	globalTasksRef: React.MutableRefObject<Map<string, UITask>>;
	uiTasksRef: React.MutableRefObject<UITask[]>;
	runtime: { releaseDispatched: (id: string) => void; activeDeleteKeys: Set<string> };
	dispatch: React.Dispatch<UiTaskAction>;
	scheduleTaskBatch: (
		taskDefinitions: TaskDefinition[],
		options?: {
			maxRateLimitWaitMs?: number;
			showNotification?: (notification: TaskNotification) => void;
		},
	) => Promise<void>;
}) {
	const { globalTasksRef, uiTasksRef, runtime, dispatch, scheduleTaskBatch } = deps;

	const retryTasks = async (taskIds: string[]): Promise<void> => {
		const ids = new Set(taskIds);
		const existingTasks = uiTasksRef.current.filter((task) => ids.has(task.id));
		if (existingTasks.length === 0) return;

		for (const task of existingTasks) {
			runtime.releaseDispatched(task.id);
			globalTasksRef.current.delete(task.id);

			if (task.taskType === CONCURRENT_TASK_TYPES.DELETE_RECORD) {
				const key = makeDeleteKeyFromMeta(task.execution.apiTask.metadata as Record<string, unknown>);
				if (key) runtime.activeDeleteKeys.delete(key);
			}
		}

		const filtered = uiTasksRef.current.filter((task) => !ids.has(task.id));
		// scheduleTaskBatch deduplicates against this ref, so update it in the
		// same operation as the reducer dispatch instead of waiting for useEffect.
		uiTasksRef.current = filtered;
		dispatch(uiActions.replaceAll(filtered));

		const retryStamp = Date.now();
		const definitions: TaskDefinition[] = existingTasks.map((task, index) => ({
			id: `${task.id}-retry-${retryStamp}-${index}`,
			taskType: task.taskType,
			metadata: task.execution.apiTask.metadata as TaskDefinition['metadata'],
			executeFn: task.execution.executeFn,
			onComplete: task.execution.onComplete,
			onAccepted: task.execution.onAccepted,
		}));

		await scheduleTaskBatch(definitions, {
			showNotification: existingTasks[0].execution.showNotification,
		});
	};

	const retryTask = async (taskId: string): Promise<void> => retryTasks([taskId]);

	return { retryTask, retryTasks };
}
